// biome-ignore lint/style/useNodejsImportProtocol: <Cannot access "node:querystring.parse" in client code>
import querystring from 'querystring'
import { Box, Container, Pagination, Typography } from '@mui/material'
// biome-ignore lint/style/useImportType: <This is not a type-only import>
import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { PushHistoryFunction } from '../interfaces/index'
import type { SearchPerkResult } from '../types'

import { useAuth } from 'react-oidc-context'
import { Loader } from '../BaseComponents'
import { SEARCH_FILTERS } from '../Constants'
import { FullWidthLayout } from '../Layouts'
import PermissionsWarning from '../components/PermissionsWarning'
import AddPerkBanner from '../components/perks/AddPerkBanner'
import PerkModal from '../components/perks/PerkModal'
import PerksFilters from '../components/perks/PerksFilters'
import PerksResultsTypesense from '../components/perks/PerksResultsTypesense'
import {
  Layout,
  ResultsDescription,
  SortDropdown,
  TextSearch,
} from '../components/shared'
import { AppAPIClient, useAnalytics, useToastMessage } from '../lib/index'
import {
  getResults,
  handleClearFilters,
  handlePageChange,
  handleParamChange,
  handleTextChange,
} from '../shared/helpers'
import usePerksModal from './hooks/usePerksModal'

// Access vite-defined global constants
declare const BASENAME: string

// Perks Page Configuration
const ALLOWED_QUERIES = [
  'page',
  'text',
  'techstarsCompany',
  'partner',
  'categories',
  'sort',
]
const PATH_NAME = '/'
const sortOptions = [
  { label: 'Relevance', value: 'relevance' },
  { label: 'Alphabetical', value: 'alphabetical' },
  { label: 'Expiring Soonest', value: 'expiring_soonest' },
  { label: 'Most Popular', value: 'most_popular' },
  { label: 'Most Value', value: 'most_value' },
  { label: 'Newest', value: 'newest' },
]
const DEFAULT_SORT = 'relevance'

interface PerksListingProps {
  canReadPerks: boolean | null
}

const Perks = ({ canReadPerks }: PerksListingProps) => {
  // globals
  const { sendEvent } = useAnalytics()
  const auth = useAuth()
  const [api] = useState(new AppAPIClient(auth))
  const location = useLocation()
  const navigate = useNavigate()
  const { id: perkId } = useParams<{ id?: string }>()

  useEffect(() => {
    api.trackAnalytics('VIEWED_PERKS_LISTING')
  }, [api])

  // data fetching
  const [loading, setLoading] = useState(true)
  const [availableCategories, setAvailableCategories] = useState<
    string[] | null
  >(null)

  // rendering results
  const [results, setResults] = useState<SearchPerkResult[] | null>(null)

  const [pagination, setPagination] = useState({
    page: 1,
    start: 0,
    end: 0,
    total: 0,
    pageCount: 0,
  })

  const queryParams = querystring.parse(location.search.slice(1))
  const [filterSelections, setFilterSelections] = useState(queryParams)
  // events
  useEffect(() => {
    if (!loading) {
      sendEvent(`search-results-count-${PATH_NAME}`, results?.length)
    }
  }, [results, loading, sendEvent])

  useEffect(() => {
    sendEvent(`search-changed-${PATH_NAME}`, location.search)
  }, [location.search, sendEvent])

  const pushHistory: PushHistoryFunction = (query: Record<string, unknown>) => {
    const fullPath = `${BASENAME}${location.pathname}`
    history.pushState(
      {
        references: availableCategories,
      },
      '',
      `${fullPath}?${querystring.stringify(
        query as querystring.ParsedUrlQueryInput,
      )}`,
    )
    setFilterSelections(query as querystring.ParsedUrlQuery)
    getResults(
      null,
      query, // Use the updated query instead of stale queryParams
      api,
      setPagination,
      setResults,
      setLoading,
      [],
      ALLOWED_QUERIES,
      SEARCH_FILTERS.PERKS_SEARCH_ROUTE,
    )
  }

  const { renderToastContainer } = useToastMessage()
  const {
    isModalOpen,
    modalPerk,
    toggleModal,
    openModalForPerkId,
    closeModal,
  } = usePerksModal(pagination, availableCategories, api)

  // fetch initial results
  // biome-ignore lint/correctness/useExhaustiveDependencies: <See comment below>
  useEffect(() => {
    getResults(
      null,
      queryParams,
      api,
      setPagination,
      setResults,
      setLoading,
      [],
      ALLOWED_QUERIES,
      SEARCH_FILTERS.PERKS_SEARCH_ROUTE,
      setAvailableCategories,
    )

    return () => {
      api.close()
    }
  }, [api, location]) // ⚠️ fixing the dependencies array breaks this due to a canceled request - handling implementing abort controller

  // setup cancel token if we destroy this component
  useEffect(() => {
    return () => {
      api.close()
    }
  }, [api])

  // Handle URL parameter changes for direct navigation to perks
  useEffect(() => {
    const handlePerkId = async () => {
      if (perkId) {
        // Check if modal is already open with the correct perk to avoid unnecessary API calls
        if (isModalOpen && modalPerk?.id === perkId) {
          // Modal is already open with the correct perk, no need to fetch again
          return
        }

        // Handle both search results and direct navigation
        const found = await openModalForPerkId(perkId)
        if (!found) {
          // Handle case where perk couldn't be loaded (404, network error, etc.)
          console.warn(`Perk ${perkId} could not be loaded`)
          navigate('/')
        }
      } else if (!perkId && isModalOpen) {
        // URL changed to remove perk ID (browser back button), close modal
        closeModal()
      }
    }

    handlePerkId()
  }, [perkId, openModalForPerkId, isModalOpen, modalPerk, navigate, closeModal])

  if (canReadPerks === null) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h5">Loading...</Typography>
      </Box>
    )
  }

  if (canReadPerks === false) {
    return <PermissionsWarning />
  }

  if (availableCategories === null) return <Loader />

  return (
    <FullWidthLayout>
      <PerkModal open={isModalOpen} perk={modalPerk} onClose={closeModal} />
      {renderToastContainer()}
      <Container maxWidth="xl">
        <Layout
          banner={<AddPerkBanner />}
          clearFiltersHandler={() => handleClearFilters(pushHistory)}
          filters={
            <PerksFilters
              filterSelections={filterSelections}
              availableCategories={availableCategories}
              clearFiltersHandler={() => handleClearFilters(pushHistory)}
              onChangeHandler={(
                name: string,
                value: string | string[] | boolean | number,
                allowMultiple: boolean,
              ) =>
                handleParamChange(
                  name,
                  value,
                  allowMultiple,
                  filterSelections,
                  ALLOWED_QUERIES,
                  pushHistory,
                )
              }
            />
          }
          isLoading={loading}
          header={'Search Perks'}
          pagination={
            results?.length !== 0 && (
              <Pagination
                count={pagination?.pageCount}
                page={pagination?.page}
                onChange={(e: React.ChangeEvent<unknown>, newPage: number) =>
                  handlePageChange(
                    e as React.MouseEvent,
                    newPage,
                    filterSelections,
                    api,
                    setPagination,
                    pushHistory,
                    SEARCH_FILTERS.PERKS_SEARCH_ROUTE,
                  )
                }
                defaultPage={1}
                boundaryCount={2}
                shape="rounded"
              />
            )
          }
          textSearch={
            <TextSearch
              label="Search"
              defaultValue={
                Array.isArray(filterSelections?.text)
                  ? filterSelections.text.join(' ')
                  : filterSelections?.text
              }
              onSubmitHandler={(
                e: React.FormEvent<HTMLFormElement>,
                textValue: string,
              ) =>
                handleTextChange(
                  e as unknown as React.ChangeEvent<HTMLInputElement>,
                  textValue,
                  filterSelections,
                  pushHistory,
                )
              }
            />
          }
          resultsDescription={
            <ResultsDescription
              start={pagination?.start}
              end={pagination?.end}
              total={pagination?.total}
              description={'perks'}
              textSearch={filterSelections?.text}
            />
          }
          sortDropdown={
            <SortDropdown
              handleonSelectChange={(value: string) =>
                handleParamChange(
                  'sort',
                  value,
                  false,
                  filterSelections,
                  ALLOWED_QUERIES,
                  pushHistory,
                )
              }
              sortOptions={sortOptions}
              sortValue={(filterSelections.sort as string) ?? DEFAULT_SORT}
            />
          }
          results={
            <PerksResultsTypesense
              results={results || []}
              toggleModal={toggleModal}
              pagination={pagination}
            />
          }
        />
      </Container>
    </FullWidthLayout>
  )
}

export default Perks
